import React, { useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SONG_SLOT_MAX_LENGTH } from '@soundxyz/utils/constants/constants';
import { getCommentTimeStamp } from '@soundxyz/utils/helpers';
import { styled } from '@components-shared/src/stitches';
import { EarliestCommentCard } from '@components/CommentCard/EarliestCommentCard';
import { Avatar } from '@components/basics/Images/Avatar';
import { HoverCard } from '@components/main-components/hover-card';
import { contentStyles } from '@components/main-components/shared';
import { MediaQuery } from '@context/mediaQuery';
import type { WaveformCommentsReleaseInfoFragment } from '@graphql/reactQuery';
import { getFragment, WaveformCommentInfoFragmentDoc } from '@graphql/reactQuery';
import { WAVEFORM_AVATAR_SIZE, WAVEFORM_AVATAR_SIZE_COLLAPSED } from '@utils/constants';
import { getCommentPosition } from '@utils/helpers';
import type { PlayCommentArgs } from './WaveformComments';

export type UserCommentProps = {
  nft: WaveformCommentsReleaseInfoFragment['topNftsWithComment'][number];
  release: WaveformCommentsReleaseInfoFragment;
  width: number;
  isOpen: boolean;
};

export const AvatarWithComment = React.memo(function AvatarWithComment({
  nft: nftFrag,
  release,
  isOpen,
  width,
  setRealTimeHoveredCommentId,
  playComment,
  isOverCollapseThreshold = false,
}: {
  playComment: (args: PlayCommentArgs) => void;
  setRealTimeHoveredCommentId: (id: string | null) => void;
  isOverCollapseThreshold?: boolean;
} & UserCommentProps) {
  const { tablet } = MediaQuery.useContainer();

  const avatarSize =
    !tablet && isOverCollapseThreshold ? WAVEFORM_AVATAR_SIZE_COLLAPSED : WAVEFORM_AVATAR_SIZE;

  const nft = getFragment(WaveformCommentInfoFragmentDoc, nftFrag);

  const commentTimestamp = getCommentTimeStamp({
    songSlot: nft.songSlot,
    totalSlots: SONG_SLOT_MAX_LENGTH,
    trackDuration: release.track.duration,
  });

  const comment = {
    id: nft.id,
    avatarUrl: nft.avatarUrl,
    commentText: nft.comment.message,
    commentTimestamp: commentTimestamp,
    publicAddress: nft.ownerPublicAddress,
    username: nft.ownerUsername,
    trackDuration: release.track.duration,
    updatedAt: nft.updatedAt,
    isArtist: !!nft.ownerArtistName,
    webappUri: nft.ownerWebappUri,
    ownerReleaseNftCount: nft.ownerReleaseNftCount,
  };

  const commentPosition = getCommentPosition({
    availableWidth: width,
    avatarSize,
    commentTimestamp,
    trackDuration: release?.track?.duration,
  });

  const onAvatarClick = useCallback(() => {
    playComment({
      commentTimestamp,
      commentId: nft.id,
    });
  }, [commentTimestamp, nft.id, playComment]);

  if (!tablet) {
    return (
      <>
        <AnimatePresence mode="wait">
          {isOpen && (
            <MobileCommentContainer
              initial={{ opacity: 0 }}
              exit={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.1, delay: 0 }}
            >
              <MobileCommentWrapper>
                <EarliestCommentCard comment={comment} />
              </MobileCommentWrapper>
            </MobileCommentContainer>
          )}
        </AnimatePresence>
        <AvatarContainer
          css={{
            left: `${commentPosition}px`,
          }}
          key={nft.id}
          isOpen={isOpen && !isOverCollapseThreshold}
          after={!isOverCollapseThreshold}
        >
          {isOverCollapseThreshold ? (
            <CollapsedAvatar />
          ) : (
            <Avatar
              src={nft.avatarUrl}
              size={WAVEFORM_AVATAR_SIZE}
              walletAddress={nft.ownerPublicAddress}
              disableLink
              webappUri={nft.ownerWebappUri}
              borderRadius="50%"
              priority
            />
          )}
        </AvatarContainer>
      </>
    );
  }

  return (
    <HoverCard key={nft.id} open={isOpen}>
      <AvatarContainer
        css={{
          left: `${commentPosition}px`,
        }}
        key={nft.id}
        isOpen={isOpen}
        after
      >
        <HoverCard.StyledContent
          side="top"
          hideWhenDetached
          onMouseEnter={() => setRealTimeHoveredCommentId(nft.id)}
          onMouseLeave={() => setRealTimeHoveredCommentId(null)}
          sideOffset={26}
        >
          <EarliestCommentCard comment={comment} />
        </HoverCard.StyledContent>
        <HoverCard.Trigger
          onClick={onAvatarClick}
          onMouseEnter={() => setRealTimeHoveredCommentId(nft.id)}
          onMouseLeave={() => setRealTimeHoveredCommentId(null)}
        >
          <Avatar
            src={nft.avatarUrl}
            size={WAVEFORM_AVATAR_SIZE}
            walletAddress={nft.ownerPublicAddress}
            disableLink
            webappUri={nft.ownerWebappUri}
            borderRadius="50%"
          />
        </HoverCard.Trigger>
      </AvatarContainer>
    </HoverCard>
  );
});

const AvatarContainer = styled('div', {
  pointerEvents: 'all',
  position: 'absolute',
  marginTop: 36,
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',

  '@tablet': {
    marginTop: 36,
  },

  variants: {
    isOpen: {
      true: {
        transform: 'scale(1.5)',
        zIndex: '$above1',
      },
    },

    after: {
      true: {
        '&::after': {
          content: '',
          position: 'absolute',
          height: WAVEFORM_AVATAR_SIZE,
          width: WAVEFORM_AVATAR_SIZE,
          border: '1px solid rgba(255,255,255,0.5)',
          top: 0,
          left: 0,
          borderRadius: '50%',
          pointerEvents: 'none',
        },
      },
    },
  },
});

const MobileCommentContainer = styled(motion.div, {
  position: 'absolute',
  bottom: -12,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: '$tooltip',
});

const MobileCommentWrapper = styled('div', {
  ...contentStyles,
  height: 'unset',
});

const CollapsedAvatar = styled('div', {
  width: WAVEFORM_AVATAR_SIZE_COLLAPSED,
  height: WAVEFORM_AVATAR_SIZE_COLLAPSED,
  borderRadius: '50%',
  backgroundColor: '$fuchsia500',
  marginTop: 10,
});
